import api from '@/api';
import { CART_RESET, CART_CLEAR_USER_ORDERS } from '@/apps/cart/store/types';
import { LOCAL_FCM_TOKEN } from '@/config/constants';
import {
  AUTH_CHECK_PHONE,
  AUTH_CUSTOMER_TOKEN,
  AUTH_GUEST_TOKEN,
  AUTH_KIOSK_TOKEN,
  AUTH_LOGOUT,
  AUTH_MODAL,
  AUTH_PROFILE,
  SET_USER_TYPE,
  AUTH_FORGOT_PASSWORD,
  AUTH_RESET_PASSWORD,
  AUTH_GUEST_INFO,
  AUTH_TOKEN,
  CHECK_CUSTOMER_PROFILE,
  PUSH_DEVICE_FCM_TOKEN,
  RESEND_DEVICE_FCM_TOKEN,
  ACCOUNT_REMOVAL_REQUESTED,
  ACCOUNT_REMOVAL_CONFIRMED,
} from './types';

export default {
  /**
   * Logout authenticated user
   */
  [AUTH_LOGOUT]: async ({ commit, dispatch }) => {
    commit(AUTH_LOGOUT);
    dispatch(CART_RESET);
    dispatch(CART_CLEAR_USER_ORDERS);
    await dispatch(AUTH_GUEST_TOKEN);
  },

  /**
   * Get customer auth token based on passed phone and password
   */
  [AUTH_CUSTOMER_TOKEN]: async ({ commit, dispatch }, credentials) => {
    const resp = await api.auth.getCustomerToken({ credentials });

    commit(SET_USER_TYPE, 'customer');
    commit(AUTH_CUSTOMER_TOKEN, resp.data.token);

    dispatch(RESEND_DEVICE_FCM_TOKEN);

    return dispatch(AUTH_PROFILE);
  },

  /**
   * Get guest auth token. No credentials required.
   */
  [AUTH_GUEST_TOKEN]: async ({ commit }) => {
    const resp = await api.auth.getGuestToken({});

    commit(SET_USER_TYPE, 'guest');
    commit(AUTH_GUEST_TOKEN, resp.data);

    return resp;
  },

  /**
   * Check if phone exists and store it.
   *
   * @property {String} phone - user's phone
   * @return {Boolean} "true" - if user already register
   */
  [AUTH_CHECK_PHONE]: async ({ commit }, { phone }) => {
    const { data: { profileExists } } = await api.auth.checkPhone({ phone });

    commit(AUTH_CHECK_PHONE, phone);

    return profileExists;
  },

  /**
   * Check customer profile exist or not.
   * @param store
   * @param { number } phone
   * @return {Promise<boolean>}
   */
  [CHECK_CUSTOMER_PROFILE]: async (store, { phone }) => {
    const { data: { profileExists } } = await api.auth.checkCustomer({ phone });

    return profileExists;
  },

  /**
   * Retrieves user profile
   */
  [AUTH_PROFILE]: async ({ commit }) => {
    const resp = await api.auth.customerProfile({});

    commit(AUTH_PROFILE, resp.data);

    return resp;
  },

  /**
   * Show auth modal
   *
   * @prop {String} name - name of modal
   * @prop {Boolean} value - display modal or not
   */
  [AUTH_MODAL]: ({ commit }, { name, value }) => {
    commit(AUTH_MODAL, { name, value });
  },
  [AUTH_KIOSK_TOKEN]: async ({ commit }, { phone }) => {
    const resp = await api.auth.getKioskToken({ credentials: { phone } });

    commit(AUTH_CUSTOMER_TOKEN, resp.data.token);
    commit(AUTH_CHECK_PHONE, phone);

    return resp;
  },

  /**
   * Send requiest to reset password
   *
   * @property {String} email - user's email
   * @return {Object} resp - response of reset password
   */
  [AUTH_FORGOT_PASSWORD]: async ({ commit }, { email }) => {
    /** @type {{data: {detail: string}}} */
    const resp = await api.auth.resetPassword({ email });

    commit(AUTH_FORGOT_PASSWORD, { email, message: resp.data.detail });

    return resp;
  },

  /**
   * Send request to confirm reset password
   *
   * @property {String} uid - uid from email
   * @property {String} token - token from email
   * @property {String} password - user's new password
   * @return {Object} resp - response of confirm reset password
   */
  [AUTH_RESET_PASSWORD]: async ({ commit }, { uid, token, password }) => {
    const resp = await api.auth.confirmResetPassword({ uid, token, password });

    commit(AUTH_RESET_PASSWORD, password);

    return resp;
  },
  /**
   * Store guest auth data and guest phone
   */
  [AUTH_GUEST_INFO]: ({ commit }, { accountData }) => {
    commit(AUTH_GUEST_INFO, accountData);
  },

  /**
   * Store auth token
   */
  [AUTH_TOKEN]: ({ commit }, data) => {
    commit(AUTH_TOKEN, data);
  },

  /**
   * Push device FCM token.
   * @param store
   * @param token
   * @return {Promise<void>}
   */
  [PUSH_DEVICE_FCM_TOKEN]: async (store, { token }) => {
    const localToken = localStorage.getItem(LOCAL_FCM_TOKEN, token);

    if (localToken === token) {
      return;
    }

    localStorage.setItem(LOCAL_FCM_TOKEN, token);

    try {
      await api.auth.pushDeviceFcmToken(token);
    } catch (err) {
      console.log(JSON.stringify(err));
    }
  },

  /**
   * Resend device FCM token for user binding.
   * @param getters
   * @param dispatch
   * @return {Promise<void>}
   */
  [RESEND_DEVICE_FCM_TOKEN]: async ({ getters, dispatch }) => {
    if (getters.isMobilePlatform && getters.isCordovaReady) {
      const token = localStorage.getItem(LOCAL_FCM_TOKEN);

      dispatch(PUSH_DEVICE_FCM_TOKEN, { token });
    }
  },

  [ACCOUNT_REMOVAL_REQUESTED]: async () => {
    try {
      await api.auth.requestAccountRemoval();
    } catch (e) {
      const errorMessage = e.response.data.data[0] || e.response.data.detail;

      throw new Error(errorMessage);
    }
  },

  [ACCOUNT_REMOVAL_CONFIRMED]:
    async ({ dispatch }, code) => {
      await api.auth.confirmAccountRemoval(code);
      await dispatch(AUTH_LOGOUT);
    },
};



// WEBPACK FOOTER //
// ./src/apps/auth/store/actions.js